import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import isFunction from 'lodash-es/isFunction';
import { ForwardedRef, startTransition, Suspense, useState } from 'react';

import LazyComponent from '../partials/LazyComponent';
import * as BlocksComponents from './index';

import styles from '../../styles/blocks/blocks.module.css';

interface BlocksProps<T> {
    blocks: T[];
    withoutMaxWidth?: boolean;
    className?: string | null;
    blockClassName?: string | ((block: T, index: number) => string) | null;
}

function Blocks<T extends Block = Block>({
    ref: containerRef,
    blocks,
    withoutMaxWidth = false,
    className = null,
    blockClassName = null,
}: BlocksProps<T> & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const [emptyBlocks, setEmptyBlocks] = useState<(number | string)[]>([]);
    const blocksCount = blocks.length;
    const blocksFillHandlers = [...Array(blocksCount).keys()].map((index) => ({
        onFilled: () => {
            startTransition(() => {
                setEmptyBlocks((prevEmptyBlocks) =>
                    prevEmptyBlocks.indexOf(index) !== -1
                        ? prevEmptyBlocks.filter((i) => i !== index)
                        : prevEmptyBlocks,
                );
            });
        },
        onEmpty: () => {
            startTransition(() => {
                setEmptyBlocks((prevEmptyBlocks) =>
                    prevEmptyBlocks.indexOf(index) === -1
                        ? [...prevEmptyBlocks, index]
                        : prevEmptyBlocks,
                );
            });
        },
    }));

    if (blocks.length === 0) {
        return null;
    }
    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withoutMaxWidth]: withoutMaxWidth,
                },
                className,
            ])}
            ref={containerRef}
        >
            {blocks.map((block, index) => {
                const { id = index, uuid = null, presentation, theme } = block || {};
                const { type, ...blockProps } = block || {};
                const BlockComponent = getComponentFromName(BlocksComponents, type);
                return BlockComponent !== null ? (
                    <div
                        key={`block-${id}-${type}-${uuid}`}
                        data-presentation={presentation}
                        data-theme={theme}
                        className={classNames([
                            'block',
                            `block-${type}`,
                            { [styles.empty]: emptyBlocks.indexOf(id) !== -1 },
                            isFunction(blockClassName)
                                ? blockClassName(block, index)
                                : blockClassName,
                        ])}
                    >
                        <LazyComponent
                            component={BlockComponent}
                            id={id as string}
                            uuid={uuid}
                            withoutMaxWidth={withoutMaxWidth}
                            {...blockProps}
                            {...blocksFillHandlers?.[index]}
                        />
                    </div>
                ) : null;
            })}
        </div>
    );
}

export default Blocks;
